import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import StudioShell from "@/components/StudioShell";
import { IdeaBrief } from "@/components/MoodboardExtras";
import { toast } from "sonner";
import { ArrowRight, Sparkles } from "lucide-react";

export default function DesignDNA() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/projects/${id}`).then(r => setProject(r.data)).catch(e => toast.error(apiError(e, "Could not load project"))); }, [id]);

  if (!project) return <StudioShell><div className="p-12">Loading…</div></StudioShell>;
  const dna = project.dna;
  if (!dna) return <StudioShell projectContext={project}><div className="p-12 text-sm text-stone-500">Approve a direction first.</div></StudioShell>;

  const adjustDna = async (brief) => {
    setBusy(true);
    try {
      await api.post(`/projects/${id}/select-direction`, { direction_id: project.selected_direction_id, brief });
      const r = await api.get(`/projects/${id}`);
      setProject(r.data);
      toast.success("Design DNA adjusted to your notes");
    } catch (e) { toast.error(apiError(e, "Could not adjust the DNA")); }
    setBusy(false);
  };

  const generateMoodboards = async () => {
    setBusy(true);
    toast.info("Composing room mood boards…");
    try {
      await api.post(`/projects/${id}/generate-moodboards`);
      navigate(`/project/${id}/moodboards`);
    } catch { toast.error("Failed to generate mood boards"); }
    setBusy(false);
  };

  const isDark = (hex) => {
    if (!hex) return false;
    const c = hex.replace("#","");
    if (c.length < 6) return false;
    const r = parseInt(c.substr(0,2),16), g = parseInt(c.substr(2,2),16), b = parseInt(c.substr(4,2),16);
    return (r*299 + g*587 + b*114) / 1000 < 130;
  };

  return (
    <StudioShell projectContext={project}>
      <div className="px-6 sm:px-12 py-12 max-w-[1400px] mx-auto">
        <div className="eyebrow mb-3">Codified Design System</div>
        <h1 className="editorial-heading text-4xl sm:text-6xl mb-3">{dna.design_direction}</h1>
        <div className="flex flex-wrap gap-3 mb-14">
          {(dna.personality||[]).map(p => <span key={p} className="text-xs font-mono-lab uppercase tracking-wider text-stone-500 border border-stone-200 px-3 py-1">{p}</span>)}
        </div>

        <Section title="01 / Chromatic Palette">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {(dna.palette||[]).map((p,i)=>(
              <div key={`${p.hex}-${p.name}`} className="swatch-chip flex flex-col justify-between p-4" style={{ background: p.hex, color: isDark(p.hex) ? "#F5F5F0" : "#1A1A18" }} data-testid={`dna-swatch-${i}`}>
                <div className="eyebrow" style={{ opacity: 0.8 }}>{p.hex}</div>
                <div className="text-sm font-medium">{p.name}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="02 / Material Palette">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(dna.materials||{}).map(([k,v]) => (
              <div key={k} className="border-l-2 border-black pl-5 py-2">
                <div className="eyebrow mb-1">{k}</div>
                <div className="font-serif-editorial text-xl">{v}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="03 / Forms & Lighting">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="eyebrow mb-2">Forms</div>
              <p className="font-serif-editorial text-2xl leading-tight">{dna.forms}</p>
            </div>
            <div>
              <div className="eyebrow mb-2">Lighting</div>
              <p className="font-serif-editorial text-2xl leading-tight">{dna.lighting}</p>
            </div>
          </div>
        </Section>

        <Section title="04 / Design Keywords">
          <div className="flex flex-wrap gap-3">
            {(dna.keywords||[]).map(k => <span key={k} className="font-serif-editorial text-2xl italic text-stone-700">{k}<span className="text-stone-300 mx-3">·</span></span>)}
          </div>
        </Section>

        <Section title="05 / Avoid">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-stone-600">
            {(dna.avoid||[]).map(a => <li key={a} className="flex items-center gap-3"><span className="w-4 h-px bg-stone-400"/> {a}</li>)}
          </ul>
        </Section>

        <div className="mt-14">
          <IdeaBrief index="dna" value={project.briefs?.dna} busy={busy} onRegenerate={adjustDna}
            label="Adjust the DNA with your own ideas" cta="Adjust Design DNA"
            placeholder="e.g. Push the palette warmer, replace marble with travertine, add cane and brass accents, softer lighting…" />
        </div>
        <div className="mt-6 flex flex-wrap gap-4">
          <button onClick={generateMoodboards} disabled={busy} data-testid="dna-generate-moodboards" className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-sm disabled:opacity-50">
            <Sparkles size={16} /> Generate Room Mood Boards <ArrowRight size={16} />
          </button>
          <button onClick={()=>navigate(`/project/${id}/directions`)} className="inline-flex items-center gap-2 px-6 py-3 border border-stone-300 text-sm" data-testid="dna-back-directions">Change Direction</button>
        </div>
      </div>
    </StudioShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-12">
      <div className="eyebrow mb-6">{title}</div>
      {children}
      <div className="hairline mt-10" />
    </div>
  );
}
